import { Controller } from "@hotwired/stimulus"

// Session-Timeout-Vorwarnung im Backend (Port aus tiptop 19e1543fc / atlastitan 8c7ef77e7,
// fuer POLYGON verschlankt). Devise beendet die Sitzung nach `timeout` ms Inaktivitaet
// (serverseitig: Zeit seit dem letzten Request). Kurz vorher fragt der Controller die
// echte Restzeit ab (GET verlaengert nicht — andere Tabs/Ajax-Requests zaehlen mit) und
// zeigt ggf. den Dialog mit "Angemeldet bleiben" (POST verlaengert die Sitzung).
export default class extends Controller {
  static values = {
    timeout: Number,     // ms, Devise.timeout_in
    warning: Number,     // ms vor Ablauf, ab dann Dialog
    url: String,         // /backend/heartbeat
    signInUrl: String,
    testMode: Boolean
  }

  static targets = ["dialog", "warning", "expired", "countdown"]

  connect() {
    this.onVisibility = this.onVisibility.bind(this)
    document.addEventListener("visibilitychange", this.onVisibility)
    // Seitenaufruf = Server-Request -> volle Laufzeit ab jetzt
    this.schedule(this.timeoutValue)
  }

  disconnect() {
    document.removeEventListener("visibilitychange", this.onVisibility)
    this.clearTimers()
  }

  // Zeitpunkt fuer die naechste Pruefung: `remainingMs` vor Ablauf minus Warnzeit
  schedule(remainingMs) {
    this.clearTimers()
    this.deadline = Date.now() + remainingMs
    const delay = Math.max(remainingMs - this.warningValue, 0)
    this.checkTimer = setTimeout(() => this.check(), delay)
  }

  clearTimers() {
    if (this.checkTimer) clearTimeout(this.checkTimer)
    if (this.countdownTimer) clearInterval(this.countdownTimer)
    this.checkTimer = null
    this.countdownTimer = null
  }

  // Hintergrund-Tabs drosseln Timer -> beim Zurueckkehren neu pruefen
  onVisibility() {
    if (document.visibilityState === "visible" && this.deadline && !this.expired) {
      if (this.deadline - Date.now() <= this.warningValue) this.check()
    }
  }

  async check() {
    if (this.testModeValue) {
      this.showWarning(Math.max(this.deadline - Date.now(), 0))
      return
    }

    let data
    try {
      const response = await fetch(this.urlValue, {
        method: "GET",
        credentials: "same-origin",
        headers: { "Accept": "application/json" }
      })
      if (response.status === 401) return this.showExpired()
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      data = await response.json()
    } catch (error) {
      // Netzwerkfehler: lokal weiterzaehlen, nicht faelschlich abmelden
      console.warn("Session-Status nicht abrufbar:", error)
      return this.showWarning(Math.max(this.deadline - Date.now(), 0))
    }

    // Remember-Me aktiv -> Devise erzwingt keinen Timeout
    if (data.remember_me || data.remaining_ms === null) return this.clearTimers()

    if (data.remaining_ms <= 0) return this.showExpired()
    if (data.remaining_ms > this.warningValue + 5000) {
      // Inzwischen anderswo verlaengert (anderer Tab, Ajax) -> neu planen
      this.hideDialog()
      return this.schedule(data.remaining_ms)
    }
    this.showWarning(data.remaining_ms)
  }

  showWarning(remainingMs) {
    this.clearTimers()
    this.deadline = Date.now() + remainingMs
    this.warningTarget.classList.remove("hidden")
    this.expiredTarget.classList.add("hidden")
    this.dialogTarget.classList.remove("hidden")
    this.renderCountdown()
    this.countdownTimer = setInterval(() => this.renderCountdown(), 1000)
  }

  renderCountdown() {
    const seconds = Math.max(Math.ceil((this.deadline - Date.now()) / 1000), 0)
    const min = Math.floor(seconds / 60)
    const sec = String(seconds % 60).padStart(2, "0")
    if (this.hasCountdownTarget) this.countdownTarget.textContent = `${min}:${sec}`
    if (seconds <= 0) this.showExpired()
  }

  showExpired() {
    this.clearTimers()
    this.expired = true
    this.warningTarget.classList.add("hidden")
    this.expiredTarget.classList.remove("hidden")
    this.dialogTarget.classList.remove("hidden")
  }

  hideDialog() {
    this.dialogTarget.classList.add("hidden")
  }

  // Button "Angemeldet bleiben"
  async extend(event) {
    if (event) event.preventDefault()
    if (this.testModeValue) {
      this.hideDialog()
      return this.schedule(this.timeoutValue)
    }

    try {
      const token = document.querySelector('meta[name="csrf-token"]')
      const response = await fetch(this.urlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": token ? token.content : ""
        }
      })
      if (response.status === 401) return this.showExpired()
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()
      this.hideDialog()
      if (data.remember_me || data.remaining_ms === null) return this.clearTimers()
      this.schedule(data.remaining_ms || this.timeoutValue)
    } catch (error) {
      console.warn("Sitzung konnte nicht verlaengert werden:", error)
    }
  }

  // Button "Neu anmelden" (nach Ablauf)
  signIn(event) {
    if (event) event.preventDefault()
    window.location.href = this.signInUrlValue || "/users/sign_in"
  }
}
